// Product brief for Securian SecureCare III / IV. See cmTwo.ts for the format
// intent.

export const secureCareBrief = `**Securian SecureCare III / IV** — cash-indemnity asset-based hybrid life with an LTC benefit.

NOTE: This is SecureCare (\`secureCareThree\` / \`secureCareFour\`), a Securian
Financial hybrid-life product. It is UNRELATED to New York Life's
\`nylSecureCare\` / \`nylJointSecureCare\` (traditional standalone ShareCare LTC).

Ground truth:
- A carrier illustration + field-mapping live in
  \`packages/shared/src/calculations/docs/securian-securecare/\`
  (SCIII_100K_3pct.pdf — Female 65, $100K single premium, 2yr acceleration +
  4yr extension, 3% compound, cash indemnity). Reconcile figures against it, and
  see the field-mapping's open issues before treating a mismatch as a bug.
  SecureCare IV has no illustration in the repo (\`hasUnverifiedModeling: true\`).

Pool model — two pools, ONE rate, drawn in order:
- **Acceleration** is the \`base_pool\`, drawn FIRST. At-issue size = Monthly
  Benefit × acceleration duration months (e.g. $4,347.25 × 24 = $104,334).
- **Extension** is the \`eob\` column, drawn only after Acceleration is exhausted.
  At-issue size = Monthly Benefit × extension duration months (e.g. × 48 =
  $208,668). Legacy policies without duration fields fall back to the stored
  dollar pools (\`policyMaximumBenefitAmount\` / \`policyContinuationBenefitAmount\`).
- Both pools inflate on the SINGLE \`policyInflationProtection\` rate at the policy
  anniversary — simple OR compound (\`isSimpleInflationProtection\`), capped at
  \`policyInflationProtectionYears\`. The Acceleration/Extension split is a
  drawdown-order diagram, not two independently-inflating pools, so the two pool
  rates are the same (the illustration's single uniformly-inflating "Total LTC
  Benefit").

Payout model — **always cash indemnity**:
- Pays the full pro-rated monthly cap as cash regardless of actual cost, drawn
  Acceleration then Extension. Cash above eligible cost accrues as
  \`indemnity_surplus\` and drains on a later shortfall month; the reserve freezes
  once both pools are exhausted.

Death benefit — static Face, PROPORTIONAL depletion:
- Flat Face (\`policyMaximumBenefitAmount\`); inflation never grows the Face.
- Reduced **in proportion to the remaining Acceleration pool** as it is drawn
  (close DB = open DB × pool-after / pool-before), NOT dollar-for-dollar — the
  un-inflated Face is smaller than the inflated acceleration draws, so
  dollar-for-dollar would floor the Face ~a year early. Proportional reaches the
  residual only as the face is (near-)fully accelerated (p.13: min DB when the
  face is "fully accelerated"). Floored at \`policyMinimumGuaranteedBenefitAmount\`.
  Extension drawdown does NOT reduce it. At 0% inflation this coincides with
  dollar-for-dollar.

Elimination period:
- Two-tier EP (home vs. facility), served via unpaid waiting days.
- **SecureCare III: no retroactive lump** — EP days are fully out-of-pocket
  (\`in-wp\` rows).
- **SecureCare IV: the EP is reimbursed retroactively** (IV p.2/p.7 — day 91
  pays "three payments for the 90-day elimination period and one payment for
  your first month going forward"). The lump lands on the row where the WP
  bucket empties, sized at days-served × the current daily rate, drawn
  Acceleration-then-Extension, once per claim. So the illustration's four
  payments appear as 3 on the EP-completing row + 1 on the next row.
- Gated on policy type, not on \`hasRetroactiveWaitingPeriodReimbursement\` —
  it is a fixed product property, so the column is ignored for SecureCare.

Things to eyeball:
- Acceleration depletes before any Extension drawdown.
- Death benefit falls in step (proportionally) with Acceleration drawdown only,
  never Extension; it is otherwise flat at the at-issue Face.
- The Face reaches the minimum-guaranteed floor as it is (near-)fully accelerated
  — not ~a year early the way dollar-for-dollar would.
- On a SecureCare IV claim, the EP-completing row shows a lump roughly three
  times the monthly cap, and the claim ends ~3 months EARLIER than the same
  policy modelled without it (those months are paid up front, not added on).
- Indemnity surplus accrues on low-cost months and drains on high-cost months.
`;
